import React, { useState, useMemo } from 'react'
import { Modal, List, Radio, Avatar, Typography, Alert, Button } from 'antd'
import {
  UserOutlined,
  CrownOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import { changeVenuePrimaryOwner } from 'src/features/AccountsSlice'

const { Text, Title } = Typography

const ChangePrimaryOwnerModal = ({
  visible,
  onClose,
  venue,
  onPrimaryOwnerChanged,
}) => {
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  const additionalUsers = useMemo(
    () => venue?.additionalUsers || [],
    [venue?.additionalUsers],
  )

  const handleCancel = () => {
    if (isSubmitting) return
    setSelectedUserId(null)
    setError('')
    onClose?.()
  }

  const handleConfirm = async () => {
    if (!venue?._id || !selectedUserId) return

    setIsSubmitting(true)
    setError('')

    try {
      const updatedVenue = await changeVenuePrimaryOwner({
        venueId: venue._id,
        newPrimaryAccountId: selectedUserId,
      })

      if (onPrimaryOwnerChanged) {
        onPrimaryOwnerChanged(updatedVenue)
      }

      setSelectedUserId(null)
      onClose?.()
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to change primary owner. Please try again.'
      setError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const primaryOwnerName = venue?.account?.name || 'Unknown Name'
  const primaryOwnerEmail = venue?.account?.emailAddress || 'Unknown email'

  return (
    <Modal
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      width={640}
      destroyOnClose
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Change Primary Owner
          </Title>
          <Text type='secondary' style={{ fontSize: 14 }}>
            Select an existing additional user to become the new primary owner
            for this venue.
          </Text>
        </div>
      }
    >
      <div style={{ marginBottom: 24 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          Current primary owner
        </Text>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: 12,
            borderRadius: 8,
            border: '1px solid #e5e7eb',
            background: '#f9fafb',
            gap: 12,
          }}
        >
          <Avatar
            icon={<CrownOutlined />}
            style={{ backgroundColor: '#fbbf24', color: '#1f2937' }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text strong>{primaryOwnerName}</Text>
              <Text
                style={{
                  fontSize: 12,
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: '#fef3c7',
                  color: '#92400e',
                }}
              >
                Primary Owner
              </Text>
            </div>
            <Text type='secondary'>{primaryOwnerEmail}</Text>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 4 }}>
          Choose a new primary owner
        </Text>
        <Text type='secondary' style={{ fontSize: 12 }}>
          Only existing additional users for this venue can be promoted to
          primary owner. The current primary owner will remain on the venue as
          an Admin.
        </Text>
      </div>

      {additionalUsers.length === 0 ? (
        <Alert
          type='info'
          showIcon
          message='No additional users available'
          description='You need at least one additional user on this venue before you can change the primary owner.'
        />
      ) : (
        <Radio.Group
          value={selectedUserId}
          onChange={e => setSelectedUserId(e.target.value)}
          style={{ width: '100%' }}
        >
          <List
            dataSource={additionalUsers}
            bordered
            style={{ borderRadius: 8, overflow: 'hidden' }}
            renderItem={user => {
              const userId = user._id?.toString?.() || user._id
              const name = user.name || 'Unknown Name'
              const email = user.emailAddress || 'Unknown email'

              return (
                <List.Item
                  key={userId}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelectedUserId(userId)}
                >
                  <Radio value={userId} style={{ width: '100%' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                      }}
                    >
                      <Avatar
                        icon={<UserOutlined />}
                        style={{ backgroundColor: '#e5e7eb', color: '#374151' }}
                      />
                      <div style={{ flex: 1 }}>
                        <Text strong>{name}</Text>
                        <br />
                        <Text type='secondary' style={{ fontSize: 12 }}>
                          {email}
                        </Text>
                      </div>
                    </div>
                  </Radio>
                </List.Item>
              )
            }}
          />
        </Radio.Group>
      )}

      {error && (
        <Alert
          type='error'
          showIcon
          style={{ marginTop: 16 }}
          message='Unable to change primary owner'
          description={error}
        />
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 24,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ExclamationCircleOutlined style={{ color: '#f59e0b' }} />
          <Text type='secondary' style={{ fontSize: 12 }}>
            This change affects who is treated as the venue&apos;s owner across
            OMS.
          </Text>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type='primary'
            onClick={handleConfirm}
            loading={isSubmitting}
            disabled={
              isSubmitting || additionalUsers.length === 0 || !selectedUserId
            }
          >
            Confirm change
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default ChangePrimaryOwnerModal
